import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  FeatureAudienceFlag,
  FeatureAudienceSection,
  FeatureAudiencesResponse,
  FeatureAudienceUpdate,
} from './types';

export const FEATURE_AUDIENCES_URL = 'feature-audiences';

export type FeatureAudienceSaveError = {
  // field -> messages, as returned by a 400 from the endpoint
  fieldErrors: Record<string, string[]> | null;
  message: string | null;
};

const replaceFlag = (
  sections: FeatureAudienceSection[],
  updated: FeatureAudienceFlag
): FeatureAudienceSection[] =>
  sections.map((section) => {
    if (section.section !== updated.section) {
      return section;
    }
    if (section.header?.flag === updated.flag) {
      return { ...section, header: updated };
    }
    return {
      ...section,
      flags: section.flags.map((f) => (f.flag === updated.flag ? updated : f)),
    };
  });

export const useFeatureAudiences = () => {
  const proxyParameters = useProxyParameters();
  // bumped after every save: a saved header can change its section's other
  // flags on the backend (continuous feedback), so the list is reloaded
  const [version, setVersion] = useState(0);
  const params = useMemo(() => ({ version }), [version]);
  const { data, status, error } = useConfirmApi<FeatureAudiencesResponse>({
    method: 'GET',
    url: FEATURE_AUDIENCES_URL,
    params,
    disabled: !proxyParameters.organization,
  });

  const [sections, setSections] = useState<
    FeatureAudienceSection[] | undefined
  >(undefined);

  // rows belong to one organization; drop them as soon as it changes. Done by
  // hand rather than with useConfirmApi's clearOnChange, which would also blank
  // the table on every post-save reload (the version bump changes the params).
  const organization = proxyParameters.organization;
  useEffect(() => {
    if (organization !== undefined) {
      setSections(undefined);
    }
  }, [organization]);

  useEffect(() => {
    if (data) {
      setSections(data.sections);
    }
  }, [data]);

  // flag names repeat across organizations, so a late save response must not
  // land in another organization's rows if the user switched meanwhile
  const shownOrganization = useRef(proxyParameters.organization);
  shownOrganization.current = proxyParameters.organization;

  const saveFlag = useCallback(
    (update: FeatureAudienceUpdate): Promise<FeatureAudienceFlag> =>
      new Promise((resolve, reject) => {
        const savedFor = proxyParameters.organization;
        ConfirmAPI.sendRequestToConfirm(
          'PATCH',
          FEATURE_AUDIENCES_URL,
          { ...proxyParameters, ...update },
          (saved, err, hardError) => {
            if (err || hardError || !saved) {
              // a 4xx arrives as the whole response; its body holds the field errors
              const body = err?.data;
              reject({
                fieldErrors:
                  body && typeof body === 'object' && !Array.isArray(body)
                    ? body
                    : null,
                message: hardError ? String(hardError) : null,
              } as FeatureAudienceSaveError);
              return;
            }
            if (savedFor === shownOrganization.current) {
              // show the saved row at once, then reload for any side effects
              setSections((current) =>
                current ? replaceFlag(current, saved) : current
              );
              setVersion((v) => v + 1);
            }
            resolve(saved);
          }
        );
      }),
    [proxyParameters]
  );

  return { sections, status, error, saveFlag };
};
